import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { usePromotion } from "../hooks/usePromotion";
import Countdown from "./Countdown";
import BuyButton from "./BuyButton";
import HeroShowcase from "./HeroShowcase";
import { PROMO_DEADLINE_TEXT } from "../content";

const MaskedLine = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <span className={`-mb-[0.14em] block overflow-hidden pb-[0.14em] ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export default function Hero() {
  const { active } = usePromotion();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const frameY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);

  return (
    <section id="start" ref={ref} className="bg-[#FAF8FC] pb-16 pt-10 sm:pb-24 sm:pt-16">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.15fr_1fr]">
        <div>
          <MaskedLine delay={0.05}>
            <p className="text-sm font-bold uppercase tracking-widest text-[#FF008B] sm:text-base">
              Kurs online dla nauczycielek i nauczycieli języka angielskiego
            </p>
          </MaskedLine>
          <h1 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-[#242344] sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>Umiem Uczyć</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="text-[#FF008B]">Angielskiego</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.4}>
            <p className="mt-5 text-lg font-bold text-[#242344] sm:text-xl">
              Cała metodyka nauczania języka angielskiego w jednym miejscu
            </p>
          </MaskedLine>
          <MaskedLine delay={0.5}>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[#34343F] sm:text-lg">
              Jak planować lekcje, uczyć mówienia i gramatyki, poprawiać błędy oraz
              zarządzać grupą? Poznaj metodykę i praktyczne pomysły w 69 lekcjach
              online dla nauczycielek i nauczycieli angielskiego
            </p>
          </MaskedLine>
          <MaskedLine delay={0.58}>
            <p
              data-testid="hero-course-params"
              className="mt-5 text-sm font-bold text-[#242344] sm:text-base"
            >
              15 modułów · 69 lekcji · 10 miesięcy dostępu
            </p>
            <p className="mt-1.5 text-sm text-[#68677A]">
              Kurs jest gotowy. Dostęp od razu po zakupie
            </p>
          </MaskedLine>

          <MaskedLine delay={0.66}>
            <div data-testid="hero-offer" className="mt-8">
              {active ? (
                <>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <del className="text-5xl font-extrabold tracking-tight text-[#34343F] decoration-[#FF008B] decoration-[5px] sm:text-6xl">
                      399 zł
                    </del>
                    <span className="rounded-2xl bg-[#FDF4F8] px-4 py-2 text-2xl font-extrabold tracking-tight text-[#FF008B] sm:text-3xl">
                      249 zł <span className="text-base font-bold">brutto</span>
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-[#34343F]">
                    Cena promocyjna — oszczędzasz 150 zł
                  </p>
                  <p className="mt-5 text-sm font-semibold text-[#242344]">
                    {PROMO_DEADLINE_TEXT}
                  </p>
                  <div className="mt-3">
                    <Countdown size="lg" />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold uppercase tracking-wide text-[#68677A]">
                    Promocja zakończona
                  </p>
                  <p className="mt-1 text-5xl font-extrabold tracking-tight text-[#242344] sm:text-6xl">
                    399 zł <span className="text-2xl font-bold">brutto</span>
                  </p>
                </>
              )}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <BuyButton testid="hero-cta-buy" location="hero" />
                <a
                  href="#program"
                  data-testid="hero-cta-program"
                  className="inline-flex min-h-[48px] items-center justify-center rounded-full border-2 border-[#242344] px-8 py-3 text-base font-bold text-[#242344] transition-colors duration-200 hover:border-[#FF008B] hover:text-[#FF008B]"
                >
                  Zobacz program
                </a>
              </div>
            </div>
          </MaskedLine>
        </div>

        <motion.div style={{ y: frameY }} className="relative lg:pl-4">
          <HeroShowcase />
        </motion.div>
      </div>
    </section>
  );
}
